import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  Row,
  Col,
  Form,
  TreeSelect,
} from 'antd';
import PropTypes from 'prop-types';

import OntraccrButton from '../../common/buttons/OnTraccrButton';
import DraggableTable from '../../projects/ProjectScheduleOfValues/DraggableTable';
import SimpleFormModal from '../../common/modals/SimpleFormModal';
import useToggle from '../../common/hooks/useToggle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import HoverHelp from '../../common/HoverHelp';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import { getShortList, updateShortList } from '../state/boards.actions';
import { isNullOrUndefined } from '../../helpers/helpers';

export default function ShortListDisplayFieldTab({
  shortList,
  fieldOptions,
  fieldMap,
}) {
  const dispatch = useDispatch();

  const { toggle: toggleDisplayField, isToggled: isToggledDisplayField } = useToggle();

  const [displayFields, setDisplayFields] = useState([]);
  const [selectedDisplayFieldTitle, setSelectedDisplayFieldTitle] = useState();
  const [selectedDisplayFieldDataIndex, setSelectedDisplayFieldDataIndex] = useState();
  const [selectedDisplayFieldIndex, setSelectedDisplayFieldIndex] = useState();

  useEffect(() => {
    try {
      setDisplayFields(JSON.parse(shortList?.displayFields || []));
    } catch (_) {
      setDisplayFields([]);
    }
  }, [shortList]);

  const onToggle = ({
    record,
    index,
  } = {}) => {
    toggleDisplayField();
    setSelectedDisplayFieldIndex(index);
    setSelectedDisplayFieldTitle(record?.title);
    setSelectedDisplayFieldDataIndex(record?.dataIndex);
  };

  const onUpsertDisplayFields = useCallback(async () => {
    const newDisplayFields = [...displayFields];
    const newDisplayField = {
      title: selectedDisplayFieldTitle,
      dataIndex: selectedDisplayFieldDataIndex,
    };

    if (!isNullOrUndefined(selectedDisplayFieldIndex)) {
      newDisplayFields[selectedDisplayFieldIndex] = newDisplayField;
    } else {
      newDisplayFields.push(newDisplayField);
    }

    const payload = {
      displayFields: newDisplayFields,
    };
    const res = await dispatch(updateShortList(shortList.id, payload));
    if (res) {
      dispatch(getShortList(shortList.id));
      onToggle();
    }
  }, [
    shortList,
    selectedDisplayFieldDataIndex,
    selectedDisplayFieldTitle,
    displayFields,
    selectedDisplayFieldIndex,
  ]);

  const onDeleteDisplayField = useCallback((index) => {
    const newDisplayFields = displayFields.filter((_, i) => i !== index);
    dispatch(updateShortList(shortList.id, { displayFields: newDisplayFields }));
  }, [shortList, displayFields]);

  const renderDataIndex = useCallback((dataIndex) => {
    if (Array.isArray(dataIndex)) {
      return dataIndex.map(renderDataIndex).join(', ');
    }
    return fieldMap[dataIndex]?.name || 'Unknown Field';
  }, [fieldMap]);

  const displayFieldColumns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'title',
    },
    {
      title: 'Field',
      dataIndex: 'dataIndex',
      render: (dataIndex) => renderDataIndex(dataIndex),
    },
    {
      title: '',
      dataIndex: 'edit',
      width: 30,
      render: (_, record, index) => (
        <BorderlessButton
          iconNode={<EditOutlined />}
          onClick={() => onToggle({ record, index })}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'delete',
      width: 30,
      render: (_, _record, index) => (
        <BorderlessButton
          iconNode={<DeleteOutlined style={{ color: 'red' }} />}
          onClick={() => onDeleteDisplayField(index)}
        />
      ),
    },
  ], [onDeleteDisplayField, renderDataIndex]);

  const onSetDisplayFields = useCallback((_, data) => {
    dispatch(updateShortList(shortList.id, { displayFields: data }));
  }, [shortList]);

  // Function filters the field options based on the selected card type and field ids
  // Users can only select one field per card type
  const filteredFieldOptions = useMemo(() => {
    // Get the selected card type ids
    const selectedCardTypeIds = new Set(
      Array.isArray(selectedDisplayFieldDataIndex)
        ? selectedDisplayFieldDataIndex?.map((field) => fieldMap[field]?.cardTypeId)
        : [fieldMap[selectedDisplayFieldDataIndex]?.cardTypeId],
    );

    // Get the selected field ids
    const selectedFieldIds = new Set(
      Array.isArray(selectedDisplayFieldDataIndex)
        ? selectedDisplayFieldDataIndex?.map((field) => fieldMap[field]?.id)
        : [fieldMap[selectedDisplayFieldDataIndex]?.id],
    );

    return fieldOptions.map((cardType) => {
      // Disable the card type if at least one of its fields is selected
      if (selectedCardTypeIds.has(cardType.value)) {
        return {
          ...cardType,
          children: cardType.children.map((child) => ({
            ...child,
            // Disable the field if it is not selected
            disabled: !selectedFieldIds.has(child.value),
          })),
        };
      }
      return cardType;
    });
  }, [fieldMap, fieldOptions, selectedDisplayFieldDataIndex]);

  return (
    <DndProvider backend={HTML5Backend}>
      <OntraccrButton
        title="Add Display Field"
        onClick={() => onToggle()}
      />
      <DraggableTable
        dataSource={displayFields}
        columns={displayFieldColumns}
        size="small"
        key="dataIndex"
        pagination={false}
        isDraggable
        setData={onSetDisplayFields}
      />
      <SimpleFormModal
        title="Edit Display Field"
        visible={isToggledDisplayField}
        onClose={() => onToggle()}
        onSave={onUpsertDisplayFields}
      >
        <Form.Item label="Title" style={{ width: '100%' }}>
          <OnTraccrTextInput
            value={selectedDisplayFieldTitle}
            onChange={(e) => setSelectedDisplayFieldTitle(e.target.value)}
          />
        </Form.Item>
        <Row align="middle" style={{ marginTop: 10, fontSize: 15 }} gutter={10}>
          <Col>
            Fields:
          </Col>
          <Col>
            <HoverHelp
              placement="topRight"
              content={(
                <div style={{ width: 250 }}>
                  Select the fields to display in the short list column. Only one custom field per card type is allowed for a column.
                  <br />
                  <br />
                  Note: if multiple fields are selected, the column will display the first field with a value.
                </div>
              )}
            />
          </Col>
        </Row>
        <TreeSelect
          style={{ width: '100%' }}
          placeholder="Select a Field"
          value={selectedDisplayFieldDataIndex}
          treeDefaultExpandAll
          multiple
          showCheckedStrategy="SHOW_PARENT"
          onChange={setSelectedDisplayFieldDataIndex}
          treeData={filteredFieldOptions}
          treeNodeFilterProp="label"
          showSearch
        />
      </SimpleFormModal>
    </DndProvider>
  );
}

ShortListDisplayFieldTab.propTypes = {
  shortList: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    displayFields: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      dataIndex: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
  }).isRequired,
  fieldOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })),
  })).isRequired,
  fieldMap: PropTypes.objectOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    cardTypeId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  })).isRequired,
};
